import { useEffect, useState, useCallback } from 'react'
import { format, startOfWeek, addDays } from 'date-fns'
import { fr } from 'date-fns/locale'
import { ChevronLeft, ChevronRight, CheckCheck, Pencil, Check, X, ClipboardList, RefreshCw, Send, Printer, AlertTriangle, Utensils, FileSignature, Clock, MapPin } from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'

interface Position { name: string; color: string }
interface ShiftInfo { startTime: string; endTime: string; position: Position | null }
interface Line {
  id: string; date: string; plannedStart: string | null; plannedEnd: string | null
  validatedStart: string | null; validatedEnd: string | null
  breakMinutes: number; netHours: number; isAbsence: boolean
  status: string; correctionNote: string | null; shift: ShiftInfo | null
}
interface Timesheet {
  id: string; weekStart: string; weekEnd: string; status: string; version: number
  totalHours: number; overtimeHours: number; mealCount: number; note: string | null
  validatedAt: string | null; signatureStatus: string; signatureEmail: string | null
  employeeSignedAt: string | null; auditLog: string | null
  employee: { id: string; firstName: string; lastName: string; weeklyHours: number; email: string | null; mealVoucherEnabled: boolean }
  lines: Line[]
}

const DAYS_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

const SIG_STATUS: Record<string, { label: string; cls: string }> = {
  none:            { label: 'Non envoyée', cls: 'bg-gray-100 text-gray-500' },
  sent:            { label: 'En attente', cls: 'bg-amber-100 text-amber-700' },
  employee_signed: { label: 'Signée (salarié)', cls: 'bg-blue-100 text-blue-700' },
  validated:       { label: 'Validée', cls: 'bg-green-100 text-green-700' },
  re_requested:    { label: 'Re-signature requise', cls: 'bg-orange-100 text-orange-700' },
}

export default function Timesheets() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }))
  const [timesheets, setTimesheets] = useState<Timesheet[]>([])
  const { current: establishment } = useEstablishment()
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [editLine, setEditLine] = useState<{ tsId: string; lineId: string; start: string; end: string; brk: number; note: string } | null>(null)
  const [sigModal, setSigModal] = useState<{ tsId: string; defaultEmail: string } | null>(null)
  const [sigEmail, setSigEmail] = useState('')
  const [sendingSig, setSendingSig] = useState(false)
  const [auditModal, setAuditModal] = useState<Timesheet | null>(null)

  const weekStr = format(weekStart, 'yyyy-MM-dd')
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  const load = useCallback(async () => {
    if (!establishment) return
    setLoading(true)
    try {
      const res = await api.get(`/timesheets?weekStart=${weekStr}&establishmentId=${establishment.id}`)
      setTimesheets(res.data)
    } finally { setLoading(false) }
  }, [weekStr, establishment])

  useEffect(() => { load() }, [load])

  async function generate() {
    if (!establishment) return
    setGenerating(true)
    try {
      await api.post('/timesheets/generate', { weekStart: weekStr, establishmentId: establishment.id })
      await load()
    } finally { setGenerating(false) }
  }

  async function validateAll() {
    if (!confirm('Valider toutes les feuilles de la semaine ?')) return
    await api.post('/timesheets/validate-all', { weekStart: weekStr, establishmentId: establishment?.id })
    await load()
  }

  async function validateOne(tsId: string) {
    const res = await api.post(`/timesheets/${tsId}/validate`)
    setTimesheets(prev => prev.map(t => t.id === tsId ? res.data : t))
  }

  async function saveLineEdit() {
    if (!editLine) return
    await api.put(`/timesheets/${editLine.tsId}/lines/${editLine.lineId}`, {
      validatedStart: editLine.start,
      validatedEnd: editLine.end,
      breakMinutes: editLine.brk,
      correctionNote: editLine.note
    })
    await load()
    setEditLine(null)
  }

  function openSigModal(ts: Timesheet) {
    setSigEmail(ts.signatureEmail || ts.employee.email || '')
    setSigModal({ tsId: ts.id, defaultEmail: ts.employee.email || '' })
  }

  async function sendSignature() {
    if (!sigModal || !sigEmail) return
    setSendingSig(true)
    try {
      await api.post(`/timesheets/${sigModal.tsId}/send-signature`, { email: sigEmail })
      await load()
      setSigModal(null)
    } finally { setSendingSig(false) }
  }

  function printSheet(tsId: string) {
    window.open(`/api/timesheets/${tsId}/print`, '_blank')
  }

  function getLineForDay(ts: Timesheet, date: Date): Line | undefined {
    return ts.lines.find(l => l.date === format(date, 'yyyy-MM-dd'))
  }

  async function acceptDispute(tsId: string) {
    const res = await api.post(`/timesheets/${tsId}/accept-dispute`)
    setTimesheets(prev => prev.map(t => t.id === tsId ? res.data : t))
  }

  async function dismissDispute(tsId: string) {
    if (!confirm('Rejeter la contestation et valider la feuille telle quelle ?')) return
    const res = await api.post(`/timesheets/${tsId}/dismiss-dispute`)
    setTimesheets(prev => prev.map(t => t.id === tsId ? res.data : t))
  }

  const draftCount = timesheets.filter(t => t.status === 'draft' && !t.note?.startsWith('⚠️')).length
  const disputedSheets = timesheets.filter(t => t.status === 'draft' && t.note?.startsWith('⚠️'))

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ClipboardList size={22} className="text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Validation des heures</h1>
        </div>
        <div className="flex items-center gap-2">
          {timesheets.length > 0 && (
            <button onClick={() => { if (!confirm('Re-synchroniser depuis le planning ?\nLes feuilles validées repassent en brouillon.')) return; generate() }}
              disabled={generating}
              className="flex items-center gap-2 border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
              <RefreshCw size={14} className={generating ? 'animate-spin' : ''} />
              {generating ? 'Mise à jour...' : 'Re-synchroniser'}
            </button>
          )}
          {timesheets.length === 0 ? (
            <button onClick={generate} disabled={generating}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              {generating ? 'Génération...' : 'Générer depuis le planning'}
            </button>
          ) : draftCount > 0 ? (
            <button onClick={validateAll}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              <CheckCheck size={16} /> Tout valider ({draftCount})
            </button>
          ) : null}
        </div>
      </div>

      {/* Sélecteur semaine */}
      <div className="flex items-center gap-4 mb-5">
        <div className="flex items-center gap-1 bg-white border rounded-lg shadow-sm">
          <button onClick={() => setWeekStart(d => addDays(d, -7))} className="p-2 hover:bg-gray-50 rounded-l-lg"><ChevronLeft size={18} /></button>
          <span className="px-4 text-sm font-medium text-gray-700 whitespace-nowrap">
            {format(weekStart, 'd MMM', { locale: fr })} – {format(addDays(weekStart, 6), 'd MMM yyyy', { locale: fr })}
          </span>
          <button onClick={() => setWeekStart(d => addDays(d, 7))} className="p-2 hover:bg-gray-50 rounded-r-lg"><ChevronRight size={18} /></button>
        </div>
        <button onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium">Cette semaine</button>
        {timesheets.length > 0 && (
          <span className="text-sm text-gray-500">
            {timesheets.filter(t => t.status === 'validated').length}/{timesheets.length} validées
          </span>
        )}
      </div>

      {/* Alerte feuilles contestées */}
      {disputedSheets.length > 0 && (
        <div className="mb-4 bg-orange-50 border border-orange-300 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-orange-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-orange-800 text-sm">
              {disputedSheets.length} feuille{disputedSheets.length > 1 ? 's' : ''} contestée{disputedSheets.length > 1 ? 's' : ''} — action requise
            </p>
            <ul className="mt-1.5 space-y-1">
              {disputedSheets.map(t => (
                <li key={t.id} className="text-xs text-orange-700">
                  <strong>{t.employee.firstName} {t.employee.lastName}</strong> : « {t.note?.replace('⚠️ Contestée par le salarié : ', '')} »
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-gray-400 text-sm">Chargement...</div>
      ) : timesheets.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-12 text-center">
          <p className="text-gray-400 text-sm mb-3">Aucune feuille d'heures pour cette semaine</p>
          <button onClick={generate} disabled={generating}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            {generating ? 'Génération...' : 'Générer depuis le planning →'}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {timesheets.map(ts => {
            const isValidated = ts.status === 'validated'
            const isDisputed = ts.status === 'draft' && ts.note?.startsWith('⚠️')
            const over = ts.totalHours > ts.employee.weeklyHours
            const sigInfo = SIG_STATUS[ts.signatureStatus] || SIG_STATUS.none
            const auditEntries = ts.auditLog ? JSON.parse(ts.auditLog) : []

            return (
              <div key={ts.id} className={`bg-white rounded-xl border overflow-hidden ${isDisputed ? 'border-orange-400' : 'border-gray-200'}`}>
                {/* Bandeau contestation */}
                {isDisputed && (
                  <div className="bg-orange-50 px-5 py-3 border-b border-orange-300">
                    {(() => {
                      const rawNote = ts.note || ''
                      const humanNote = rawNote.replace('⚠️ Contestée par le salarié : ', '').replace(/\n__corrections__::.+$/, '')
                      const hasStructured = rawNote.includes('__corrections__::')
                      return (
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            <AlertTriangle size={16} className="text-orange-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-bold text-orange-800">Contestée par le salarié</p>
                              <p className="text-sm text-orange-700 mt-0.5 whitespace-pre-wrap">{humanNote}</p>
                              {!hasStructured && (
                                <p className="text-xs text-orange-400 mt-1 italic">Correction manuelle requise — modifiez avec le crayon ✏️ puis validez</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
                            {hasStructured && (
                              <button onClick={() => acceptDispute(ts.id)}
                                className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors">
                                <Pencil size={12} /> Appliquer les corrections
                              </button>
                            )}
                            <button onClick={() => dismissDispute(ts.id)}
                              className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors">
                              <Check size={12} /> Valider tel quel
                            </button>
                          </div>
                        </div>
                      )
                    })()}
                  </div>
                )}
                {/* En-tête employé */}
                <div className={`flex items-center justify-between px-5 py-3 border-b ${isValidated ? 'bg-green-50 border-green-100' : isDisputed ? 'bg-orange-50 border-orange-100' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {ts.employee.firstName[0]}{ts.employee.lastName[0]}
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900 text-sm">{ts.employee.firstName} {ts.employee.lastName}</span>
                      <span className="text-gray-400 text-xs ml-2">({ts.employee.weeklyHours}h contrat)</span>
                    </div>
                    {isValidated && <span className="flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full font-medium"><Check size={11} /> Validée</span>}
                    {/* Signature status */}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sigInfo.cls}`}>
                      <FileSignature size={10} className="inline mr-1" />{sigInfo.label}
                    </span>
                    {/* Version + audit */}
                    {ts.version > 1 && (
                      <button onClick={() => setAuditModal(ts)}
                        className="text-xs text-gray-400 hover:text-gray-600 px-1.5 py-0.5 rounded border border-gray-200 hover:border-gray-300">
                        v{ts.version} · Audit
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Totaux */}
                    <div className="text-right hidden sm:block">
                      <p className={`text-lg font-bold ${over ? 'text-red-600' : 'text-gray-900'}`}>{ts.totalHours.toFixed(1)}h</p>
                      {ts.overtimeHours > 0 && <p className="text-xs text-red-500">+{ts.overtimeHours.toFixed(1)}h supp</p>}
                    </div>
                    {/* Repas */}
                    {ts.employee.mealVoucherEnabled && ts.mealCount > 0 && (
                      <div className="flex items-center gap-1 text-xs text-gray-500 bg-orange-50 px-2 py-1 rounded-lg">
                        <Utensils size={11} className="text-orange-400" />
                        <span>{ts.mealCount} repas</span>
                      </div>
                    )}
                    {/* Actions */}
                    <button onClick={() => printSheet(ts.id)} title="Imprimer / PDF"
                      className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                      <Printer size={15} />
                    </button>
                    <button onClick={() => openSigModal(ts)} title="Envoyer pour signature"
                      className="p-2 text-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors">
                      <Send size={15} />
                    </button>
                    {!isValidated && (
                      <button onClick={() => validateOne(ts.id)}
                        className="flex items-center gap-1.5 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 px-3 py-1.5 rounded-lg transition-colors">
                        <Check size={14} /> Valider
                      </button>
                    )}
                  </div>
                </div>

                {/* Tableau des jours */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="px-3 py-2 text-left text-xs text-gray-400 font-medium w-24">Heure</th>
                        {days.map((day, i) => {
                          const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
                          return (
                            <th key={i} className={`px-3 py-2 text-center font-semibold text-xs ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
                              {DAYS_SHORT[i]} {format(day, 'd')}
                            </th>
                          )
                        })}
                        <th className="px-3 py-2 text-center text-xs text-gray-500 font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Ligne Prévu */}
                      <tr className="border-b border-gray-50">
                        <td className="px-3 py-1.5 text-xs text-gray-400 font-medium">Prévu</td>
                        {days.map((day, i) => {
                          const line = getLineForDay(ts, day)
                          return (
                            <td key={i} className="px-2 py-1.5 text-center text-xs text-gray-500 border-r border-gray-50 last:border-0 min-w-[80px]">
                              {line && !line.isAbsence ? (
                                <span>{line.plannedStart}–{line.plannedEnd}</span>
                              ) : line?.isAbsence ? (
                                <span className="text-green-600 font-medium">{line.correctionNote?.split('—')[0]?.trim() || 'Abs.'}</span>
                              ) : <span className="text-gray-200">—</span>}
                            </td>
                          )
                        })}
                        <td />
                      </tr>
                      {/* Ligne Pointé/Validé */}
                      <tr>
                        <td className="px-3 py-1.5 text-xs text-gray-400 font-medium">
                          <div className="flex items-center gap-1">
                            <Clock size={10} />Validé
                          </div>
                        </td>
                        {days.map((day, i) => {
                          const line = getLineForDay(ts, day)
                          const isEditing = editLine?.lineId === line?.id
                          return (
                            <td key={i} className="px-2 py-1.5 text-center border-r border-gray-50 last:border-0 min-w-[80px]">
                              {line && !line.isAbsence ? (
                                isEditing ? (
                                  <div className="space-y-1">
                                    <input type="time" value={editLine.start}
                                      onChange={e => setEditLine(l => l ? { ...l, start: e.target.value } : l)}
                                      className="w-full border border-blue-300 rounded px-1 py-0.5 text-xs focus:outline-none" />
                                    <input type="time" value={editLine.end}
                                      onChange={e => setEditLine(l => l ? { ...l, end: e.target.value } : l)}
                                      className="w-full border border-blue-300 rounded px-1 py-0.5 text-xs focus:outline-none" />
                                    <input type="number" value={editLine.brk} min={0} max={120}
                                      onChange={e => setEditLine(l => l ? { ...l, brk: Number(e.target.value) } : l)}
                                      className="w-full border border-blue-300 rounded px-1 py-0.5 text-xs focus:outline-none"
                                      placeholder="Pause min" />
                                    <div className="flex gap-1 justify-center">
                                      <button onClick={saveLineEdit} className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200"><Check size={11} /></button>
                                      <button onClick={() => setEditLine(null)} className="p-1 bg-gray-100 text-gray-500 rounded hover:bg-gray-200"><X size={11} /></button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="group relative">
                                    <div className={`text-xs font-semibold ${line.status === 'corrected' ? 'text-orange-600' : line.status === 'pointed' ? 'text-blue-600' : 'text-gray-800'}`}>
                                      {line.validatedStart}–{line.validatedEnd}
                                    </div>
                                    <div className="text-xs font-bold text-indigo-700 mt-0.5">{line.netHours.toFixed(1)}h</div>
                                    {line.status === 'pointed' && (
                                      <div className="flex items-center justify-center gap-0.5 text-blue-400" title="Pointé via badgeuse">
                                        <MapPin size={9} /><span className="text-xs">Badgé</span>
                                      </div>
                                    )}
                                    {line.status === 'corrected' && <div className="text-xs text-orange-500">Corrigé</div>}
                                    {line.shift?.position && (
                                      <div className="text-xs truncate mt-0.5" style={{ color: line.shift.position.color }}>{line.shift.position.name}</div>
                                    )}
                                    {!isValidated && (
                                      <button
                                        onClick={() => setEditLine({ tsId: ts.id, lineId: line.id, start: line.validatedStart || line.plannedStart || '09:00', end: line.validatedEnd || line.plannedEnd || '17:00', brk: line.breakMinutes, note: line.correctionNote || '' })}
                                        className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200">
                                        <Pencil size={10} />
                                      </button>
                                    )}
                                  </div>
                                )
                              ) : line?.isAbsence ? (
                                <span className="text-green-600 text-xs">Absent</span>
                              ) : (
                                <span className="text-gray-200 text-xs">—</span>
                              )}
                            </td>
                          )
                        })}
                        <td className="px-3 py-2 text-center">
                          <span className={`text-sm font-bold ${over ? 'text-red-600' : 'text-gray-800'}`}>
                            {ts.totalHours.toFixed(1)}h
                          </span>
                          {ts.overtimeHours > 0 && <div className="text-xs text-red-500">+{ts.overtimeHours.toFixed(1)}h</div>}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Modal signature */}
      {sigModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSigModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-bold text-gray-900 flex items-center gap-2"><FileSignature size={18} className="text-indigo-600" /> Demande de signature</h2>
              <button onClick={() => setSigModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Email du salarié</label>
              <input type="email" value={sigEmail} onChange={e => setSigEmail(e.target.value)}
                placeholder="prenom.nom@example.com"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              <p className="text-xs text-gray-400 mt-2">Un lien de signature sera envoyé par email. Le salarié pourra consulter et signer sa feuille d'heures.</p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setSigModal(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={sendSignature} disabled={sendingSig || !sigEmail}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg">
                <Send size={14} /> {sendingSig ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal audit */}
      {auditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setAuditModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-bold text-gray-900">Journal d'audit — {auditModal.employee.firstName} {auditModal.employee.lastName}</h2>
              <button onClick={() => setAuditModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-2 max-h-80 overflow-auto">
              {(auditModal.auditLog ? JSON.parse(auditModal.auditLog) : []).map((entry: any, i: number) => (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <span className="text-gray-400 text-xs whitespace-nowrap mt-0.5">{new Date(entry.at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                  <span className="text-gray-700">{entry.action}</span>
                </div>
              ))}
              {(!auditModal.auditLog || JSON.parse(auditModal.auditLog).length === 0) && (
                <p className="text-gray-400 text-sm text-center py-4">Aucune entrée d'audit</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
